import { threshold, needsFace } from './standards.js';
import { camField, sampleFromCam, meetsReq, plateYawOk, shareSightLines } from './coverage.js';
import { camGeom, inCameraFrame, pxAt } from './optics.js';
import { entrySample } from './entries.js';
import { assessDemand } from './tasks.js';
import { planningCameraCost } from './recording.js';
import { blocked } from './geometry.js';
import { RAD, DEG, angDiff } from './constants.js';
import { createObstructionIndex } from './obstructionIndex.js';

// The search measures thousands of poses against one compiled scene, and many
// of them share a mounting point. Three things are shared, none of which changes
// an answer:
// - the exact spatial lookup the coverage calculation uses; every candidate wall
//   is still tested by the same predicate as a full scan;
// - the lookup's per-origin ray tables, reused only for the identical wall list;
// - each sight line's blocked/clear answer per mounting point. Whether the line
//   from a point to a sample is blocked depends on the walls it crosses, and any
//   wall it can cross lies within the sample's distance of the point, which every
//   view's wall list covers (wallsNear keeps 1.5 m beyond the view's range). So
//   views with different lenses or aims at one point agree on every shared sample.
// The lookup's budgets are cumulative and the shared answers take memory, so a
// fresh set replaces them before a long search can exhaust either.
const sceneLookups = new WeakMap(), LOOKUP_SCOPES = 1500, SIGHT_BYTES = 48 * 1024 * 1024;
function createPlanningLookup(walls) {
  const index = createObstructionIndex(walls), scopes = new Map(), points = new Map();
  const state = { scopes: 0, bytes: 0 };
  const sameList = (a, b) => { if (a.length !== b.length) return false; for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false; return true; };
  return {
    ...index, state,
    forRays(list, ox, oy, oz) {
      const key = ox + ':' + oy + ':' + oz + ':' + list.length, found = scopes.get(key);
      if (found && sameList(found.list, list)) return found.scope;
      const scope = index.forRays(list, ox, oy, oz); state.scopes++;
      scopes.set(key, { list, scope });
      return scope;
    },
    /** Shared answers for one point, sample set and pair of sight heights; undefined once the memory budget is spent. */
    sightLines(G, x, y, h, hFace, hPlate) {
      const key = x + ':' + y + ':' + h + ':' + hFace + ':' + hPlate;
      let grids = points.get(key);
      if (!grids) points.set(key, grids = new Map());
      let bits = grids.get(G);
      if (!bits) { if (state.bytes + G.n > SIGHT_BYTES) return undefined; bits = new Uint8Array(G.n); state.bytes += G.n; grids.set(G, bits); }
      return bits;
    }
  };
}
// Demand entries are stored in physical row order and every entry of a row has
// the same sample Y, so a pose only needs the rows whose Y is within its range;
// entries in skipped rows fail the same Y test one by one in the full loop.
const gridRows = new WeakMap();
function rowsOf(G) {
  let rows = gridRows.get(G);
  if (rows !== undefined) return rows;
  rows = null;
  const cols = G.cols, count = G.rows;
  if (Number.isInteger(cols) && Number.isInteger(count) && cols > 0 && count > 0 && count <= 1e6) {
    const start = new Int32Array(count + 1).fill(-1), y = new Float64Array(count); let ok = true, last = -1, row = -1;
    for (let i = 0; i < G.n && ok; i++) {
      const cell = G.cell?.[i] ?? i, r = Math.floor(cell / cols);
      if (cell < last || r >= count) { ok = false; break; }
      last = cell;
      if (r !== row) { for (let k = row + 1; k <= r; k++) start[k] = i; row = r; y[r] = G.sy[i]; }
      else if (G.sy[i] !== y[r]) ok = false;
    }
    if (ok) { for (let k = row + 1; k <= count; k++) start[k] = G.n; rows = { start, y, count }; }
  }
  gridRows.set(G, rows);
  return rows;
}

export function planningObstructions(S) {
  const walls = S?._sceneWalls;
  if (!Array.isArray(walls)) return undefined;
  let lookup = sceneLookups.get(walls);
  if (!lookup || lookup.state.scopes >= LOOKUP_SCOPES || lookup.state.bytes + 1 > SIGHT_BYTES) { lookup = createPlanningLookup(walls); sceneLookups.set(walls, lookup); }
  return lookup;
}
export const poseKey = c => [c.modelId, c.profileId || '', !!c.corridor, c.focal, c.x, c.y, c.h, c.pan, c.tilt, c.mountKind || 'wall'].join(':');
// The search yields so progress can be delivered, but a timer per step costs far
// more than the step: browsers stretch nested zero-delay timers to 4 ms. Yield
// through a timer at most every 50 ms; in between, an await is only a microtask.
let lastPause = 0;
export function pause() {
  const now = Date.now();
  if (now - lastPause < 50) return undefined;
  lastPause = now;
  return new Promise(r => setTimeout(r, 0));
}
export const tick = () => pause();

/** Soft framing preference. It never prohibits an acute wall angle needed for
 * an entrance or traffic lane. Compare it only after the required coverage. */
export function framingPenalty(S, cam, F = camField(S, cam)) {
  if (!F) return 1;
  const g = camGeom(S, cam);
  const upward = Math.max(0, 0.5 - Math.tan(cam.tilt * RAD) / (2 * Math.tan(g.vfov * RAD / 2)));
  const d = Math.max(1, Math.min(7, (cam.h - S.opt.hFace) / Math.max(0.08, Math.tan(cam.tilt * RAD))));
  const x = cam.x + Math.cos(cam.pan * RAD) * d, y = cam.y + Math.sin(cam.pan * RAD) * d;
  const z = cam.h - d * Math.tan(cam.tilt * RAD);
  return upward + (blocked(cam.x, cam.y, cam.h, x, y, z, F.walls) ? 0.5 : 0);
}

// Necessary centre/angle/detail conditions avoid expensive surface probes for
// impossible candidates. Final task qualification still checks every required
// surface probe and capture interval through assessDemand.
export function hitsCaptureDemand(S, cam, demand, g = camGeom(S, cam)) {
  if (!g) return false;
  if (!g.m.details?.sensorModules?.length && !demand.points.some(p => {
    const distance = Math.hypot(cam.x - p.x, cam.y - p.y, cam.h - p.height);
    const yaw = angDiff(Math.atan2(cam.y - p.y, cam.x - p.x) * DEG, demand.heading);
    const down = Math.atan2(Math.abs(cam.h - p.height), Math.max(0.01, Math.hypot(cam.x - p.x, cam.y - p.y))) * DEG;
    return yaw <= (demand.maxYaw ?? (demand.req === 'plate' ? S.opt.aPlateI : S.opt.faceYaw ?? 45)) &&
      (!(demand.req === 'plate' || needsFace(demand.req)) || down <= (demand.maxDown ?? (demand.req === 'plate' ? S.opt.aPlateT : S.opt.aFace))) &&
      pxAt(g, distance) * Math.max(0, Math.cos(yaw * RAD)) >= (demand.minPx ?? threshold(S, demand.req)) && inCameraFrame(cam, g, p.x, p.y, p.height);
  })) return false;
  return assessDemand(S, cam, demand, g).met;
}

/** Demand grids can have several independently counted requirements at a
 * point. Reuse its ray sample; never require a single camera to supply them all. */
export function measureCandidate(S, G, cam, entries, cfg, options = {}) {
  // options.scan measures with the original full wall scan, as a reference for tests.
  const lookup = options.scan ? undefined : planningObstructions(S);
  const F = camField(S, cam, undefined, lookup), hits = [];
  // Demand grids repeat a sample under several entries; answers are shared by the sample's first entry.
  let shared = false;
  if (F && lookup) for (const view of F.fields || [F]) {
    const bits = lookup.sightLines(G, view.cam.x, view.cam.y, view.cam.h, S.opt.hFace, S.opt.hPlate);
    if (bits) { shareSightLines(view, bits); shared = true; }
  }
  let previous = -1, sample;
  const visit = i => {
    if (!G.inSite[i]) return;
    const cell = G.cell?.[i] ?? i;
    if (cell !== previous) {
      previous = cell;
      sample = Math.abs(G.sx[i] - cam.x) <= F.maxG && Math.abs(G.sy[i] - cam.y) <= F.maxG ? (shared ? sampleFromCam(S, F, G.sx[i], G.sy[i], i, 0) : sampleFromCam(S, F, G.sx[i], G.sy[i])) : null;
    }
    if (sample && meetsReq(G.req[i], sample.lvl, sample.faceOk, sample.plateOk && plateYawOk(S, G, i, sample.bearing), S, sample.px, sample.vAngF, sample.facePx)) hits.push(i);
  };
  const rows = F && !options.scan ? rowsOf(G) : null;
  if (rows) { for (let r = 0; r < rows.count; r++) if (Math.abs(rows.y[r] - cam.y) <= F.maxG) for (let i = rows.start[r]; i < rows.start[r + 1]; i++) visit(i); }
  else if (F) for (let i = 0; i < G.n; i++) visit(i);
  return { ...cam, hits: Int32Array.from(hits), entryHits: Int32Array.from(entries.flatMap((p, i) =>
    (p.taskId ? hitsCaptureDemand(S, cam, p) : entrySample(S, cam, p)) ? [i] : [])),
  cost: planningCameraCost(S, cam, cfg), framing: framingPenalty(S, cam, F) };
}

/** Retain measured diversity, rather than taking a stride through untested
 * poses. Each hardware family gets complementary views, with diminishing
 * rewards for repeatedly covering the same demands. Remaining slots preserve candidates that see rare requirements or capture approaches. */
export function retainCandidates(candidates, G, cap) {
  if (candidates.length <= cap) return candidates;
  const groups = new Map(), frequency = new Uint32Array(G.n), entryFrequency = [];
  for (const c of candidates) {
    const key = c.family || c.modelId;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(c);
    for (const i of c.hits) frequency[i]++;
    for (const i of c.entryHits) entryFrequency[i] = (entryFrequency[i] || 0) + 1;
  }
  const picked = new Set();
  const buckets = [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  const quota = Math.max(1, Math.floor(cap * 0.65 / buckets.length));
  for (const [, bucket] of buckets) {
    const counts = new Uint16Array(G.n), entries = [];
    const valueOf = c => {
      let value = 0;
      for (const i of c.hits) value += 1 / (1 + counts[i]) ** 2;
      for (const i of c.entryHits) value += G.live / (1 + (entries[i] || 0)) ** 2;
      return value / (1 + c.framing * 0.08);
    };
    const take = best => {
      picked.add(best);
      for (const i of best.hits) counts[i]++;
      for (const i of best.entryHits) entries[i] = (entries[i] || 0) + 1;
    };
    const picks = Math.min(quota, bucket.length);
    // A candidate's value only falls as views are picked, so its last value is an
    // upper bound. Keep the candidates in a heap by (bound, then bucket order) and
    // re-score only the one on top: it is the pick once its fresh value still
    // leads, which is the candidate a full re-scoring would choose, ties included.
    const first = bucket.map(valueOf);
    const lazy = picks > 1 && bucket.every((c, i) => first[i] >= 0 && Number.isFinite(first[i]) && 1 + c.framing * 0.08 > 0);
    if (!lazy) {
      for (let k = 0; k < picks; k++) {
        let best, score = -Infinity;
        for (const c of bucket) {
          if (picked.has(c)) continue;
          const value = valueOf(c);
          if (value > score) { best = c; score = value; }
        }
        if (!best) break;
        take(best);
      }
      continue;
    }
    const bound = Float64Array.from(first), stamp = new Int32Array(bucket.length), heap = bucket.map((_, i) => i);
    const before = (a, b) => bound[a] > bound[b] || (bound[a] === bound[b] && a < b);
    const down = at => {
      for (;;) {
        const l = 2 * at + 1, r = l + 1; let top = at;
        if (l < heap.length && before(heap[l], heap[top])) top = l;
        if (r < heap.length && before(heap[r], heap[top])) top = r;
        if (top === at) return;
        const t = heap[at]; heap[at] = heap[top]; heap[top] = t; at = top;
      }
    };
    for (let at = (heap.length >> 1) - 1; at >= 0; at--) down(at);
    for (let k = 0; k < picks && heap.length; k++) {
      for (;;) {
        const top = heap[0];
        if (stamp[top] === k) break;
        bound[top] = valueOf(bucket[top]); stamp[top] = k; down(0);
      }
      const chosen = heap[0], last = heap.pop();
      if (heap.length) { heap[0] = last; down(0); }
      take(bucket[chosen]);
    }
  }
  const rareScore = c => {
    let score = 0;
    for (const i of c.hits) score += 1 / Math.max(1, frequency[i]);
    for (const i of c.entryHits) score += G.live / entryFrequency[i];
    return score / (1 + c.framing * 0.08);
  };
  const ranked = candidates.filter(c => !picked.has(c)).map(c => ({ c, score: rareScore(c) })).sort((a, b) => b.score - a.score || a.c.cost - b.c.cost || a.c.framing - b.c.framing || poseKey(a.c).localeCompare(poseKey(b.c)));
  for (const { c } of ranked) { if (picked.size >= cap) break; picked.add(c); }
  return [...picked].slice(0, cap);
}
